import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../ApiContext';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import { CONSTANTS } from './Configuration';

const getMaintenanceStatus = (settings, now) => {
    if (!settings?.startDateTime || !settings?.endDateTime) {
        return CONSTANTS.maintenanceStatus.NORMAL;
    }

    const start = new Date(settings.startDateTime).getTime();
    const end = new Date(settings.endDateTime).getTime();
    const configuredPriorMinutes = Number(settings.priorNotificationMinutes);
    const priorMinutes = Number.isFinite(configuredPriorMinutes)
        ? Math.max(0, Math.min(1440, configuredPriorMinutes))
        : 120;
    const warningStart = start - priorMinutes * 60 * 1000;

    if (now >= start && now <= end) {
        return CONSTANTS.maintenanceStatus.ACTIVE;
    }
    if (now >= warningStart && now < start) {
        return CONSTANTS.maintenanceStatus.WARNING_WINDOW;
    }
    if (now < start) {
        return CONSTANTS.maintenanceStatus.SCHEDULED;
    }
    return CONSTANTS.maintenanceStatus.INACTIVE;
};

const useMaintenanceStatus = (initialStatus, enabled = true) => {
    const api = useApi();
    const isMaintenanceModeEnabled = window.PRELOADED_STATE?.featureSupported?.[SUPPORTED_FEATURES.maintenanceMode] === true;
    const isEnabled = enabled && isMaintenanceModeEnabled;
    const [maintenanceStatus, setMaintenanceStatus] = useState(initialStatus);
    const [serverTimeOffset, setServerTimeOffset] = useState(0);
    const [now, setNow] = useState(Date.now());

    const refreshStatus = useCallback(async () => {
        try {
            const nextStatus = await api.maintenance.getStatus();
            setMaintenanceStatus(nextStatus);
            setServerTimeOffset(nextStatus?.serverTime ? new Date(nextStatus.serverTime).getTime() - Date.now() : 0);
            setNow(Date.now());
        } catch {
            // Keep the last known status when a background refresh fails.
        }
    }, [api]);

    const status = !isEnabled
        ? CONSTANTS.maintenanceStatus.NORMAL
        : maintenanceStatus?.status === CONSTANTS.maintenanceStatus.INACTIVE
            ? CONSTANTS.maintenanceStatus.INACTIVE
            : getMaintenanceStatus(maintenanceStatus, now + serverTimeOffset);
    const hasExpired = status === CONSTANTS.maintenanceStatus.INACTIVE;
    const pollingInterval = hasExpired || status === CONSTANTS.maintenanceStatus.NORMAL ? 60000 : 10000;

    useEffect(() => {
        if (!isEnabled) {
            return undefined;
        }

        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshStatus();
            }
        };
        window.addEventListener('focus', refreshWhenVisible);
        document.addEventListener('visibilitychange', refreshWhenVisible);

        refreshStatus();
        const intervalId = setInterval(refreshWhenVisible, pollingInterval);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', refreshWhenVisible);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [isEnabled, pollingInterval, refreshStatus]);

    // Ticks every second so status transitions (e.g. scheduled -> warning_window -> active)
    // apply as soon as they're due, instead of relying on a single boundary-timed setTimeout.
    useEffect(() => {
        if (!isEnabled || hasExpired || !maintenanceStatus?.startDateTime || !maintenanceStatus?.endDateTime) {
            return undefined;
        }

        const intervalId = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(intervalId);
    }, [isEnabled, hasExpired, maintenanceStatus?.startDateTime, maintenanceStatus?.endDateTime]);

    return { maintenanceStatus: isEnabled ? maintenanceStatus : undefined, status };
};

export default useMaintenanceStatus;